import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import AdminLayout from '../components/AdminLayout';
import CampaignDetailChart from '../components/CampaignDetailChart';
import api from '../api';
import './AdminGoogleAdsSpendingPage.css';

const AdminGoogleAdsSpendingPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [expandedSeller, setExpandedSeller] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    
    // Filtri
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filterByMerchant, setFilterByMerchant] = useState(false);


    useEffect(() => {
        // Imposta date di default (ultimi 30 giorni)
        const today = new Date();
        const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
        
        setEndDate(today.toISOString().split('T')[0]);
        setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }, []);

    const loadData = async () => {
        if (!startDate || !endDate) {
            setError('Seleziona un intervallo di date');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            const response = await api.get('/merchant/admin/google-ads/spending', {
                params: {
                    startDate,
                    endDate,
                    filterByMerchant: filterByMerchant.toString()
                }
            });
            
            setData(response.data.data);
        } catch (err) {
            setError('Errore nel caricamento dei dati');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleSellerExpansion = (sellerId) => {
        setExpandedSeller(expandedSeller === sellerId ? null : sellerId);
    };

    const openCampaignDetail = (campaign, sellerData) => {
        setSelectedCampaign({
            campaign,
            customerId: sellerData.googleAds.customerId,
            startDate,
            endDate
        });
    };

    const closeCampaignDetail = () => {
        setSelectedCampaign(null);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const formatNumber = (number) => {
        return new Intl.NumberFormat('it-IT').format(number);
    };

    const getChannelTypeLabel = (channelType, subType) => {
        if (channelType === 'SHOPPING') {
            return 'Shopping';
        } else if (channelType === 'SEARCH') {
            return 'Search';
        } else if (channelType === 'DISPLAY') {
            return 'Display';
        } else if (channelType === 'VIDEO') {
            return 'Video';
        } else if (channelType === 'PERFORMANCE_MAX') {
            return 'Performance Max';
        } else if (channelType === 'LOCAL') {
            return 'Local';
        } else if (channelType === 'SMART') {
            return 'Smart';
        }
        return channelType || 'Altro';
    };

    const getChannelTypeColor = (channelType) => {
        switch (channelType) {
            case 'SHOPPING': return 'shopping';
            case 'SEARCH': return 'search';
            case 'DISPLAY': return 'display';
            case 'VIDEO': return 'video';
            case 'PERFORMANCE_MAX': return 'performance_max';
            case 'LOCAL': return 'local';
            case 'SMART': return 'smart';
            default: return 'other';
        }
    };

    if (!user || user.role !== 'admin') {
        return <div>Accesso negato</div>;
    }

    return (
        <AdminLayout>
            <div className="admin-google-ads-spending-page">
                <div className="page-header">
                    <h1>Spesa Pubblicitaria Google Ads</h1>
                    <p>Monitora la spesa pubblicitaria di tutti i seller con account Google Ads collegati</p>
                </div>

                {/* Filtri */}
                <div className="filters-section">
                    <div className="filters-row">
                        <div className="filter-group-ads">
                            <label>Data Inizio:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="form-control"
                            />
                        </div>
                        
                        <div className="filter-group-ads">
                            <label>Data Fine:</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="form-control"
                            />
                        </div>
                        
                        <div className="filter-group-ads">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={filterByMerchant}
                                    onChange={(e) => setFilterByMerchant(e.target.checked)}
                                />
                                Solo campagne Shopping collegate al Merchant
                            </label>
                        </div>
                        

                        
                        <button 
                            className="btn btn-primary"
                            onClick={loadData}
                            disabled={loading || !startDate || !endDate}
                        >
                            {loading ? 'Caricamento...' : 'Carica Dati'}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger">
                        {error}
                    </div>
                )}

                {/* Risultati */}
                {data && (
                    <div className="results-section">
                        {/* Summary */}
                        <div className="summary-cards">
                            <div className="summary-card">
                                <h3>Seller Totali</h3>
                                <p className="summary-value">{data.summary.totalSellers}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Spesa Totale</h3>
                                <p className="summary-value">{formatCurrency(data.summary.totalSpending)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Campagne Totali</h3>
                                <p className="summary-value">{data.summary.totalCampaigns}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Intervallo</h3>
                                <p className="summary-value">
                                    {new Date(data.summary.dateRange.startDate).toLocaleDateString('it-IT')} - 
                                    {new Date(data.summary.dateRange.endDate).toLocaleDateString('it-IT')}
                                </p>
                            </div>
                        </div>

                        {/* Tabella Seller */}
                        <div className="sellers-table">
                            <h2>Dettaglio per Seller</h2>
                            
                            {data.sellers.length === 0 ? (
                                <div className="no-data">
                                    <p>Nessun seller con account Google Ads collegato trovato.</p>
                                </div>
                            ) : (
                                <div className="sellers-list">
                                    {data.sellers.map((sellerData) => (
                                        <div key={sellerData.seller.id} className="seller-card">
                                            <div className="seller-header" onClick={() => toggleSellerExpansion(sellerData.seller.id)}>
                                                <div className="seller-info-ads">
                                                    <div className="seller-logo">
                                                        {sellerData.seller.logoUrl ? (
                                                            <img src={sellerData.seller.logoUrl} alt="Logo" />
                                                        ) : (
                                                            <div className="logo-placeholder">
                                                                {sellerData.seller.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="seller-details">
                                                        <h3>{sellerData.seller.name}</h3>
                                                        <p className="seller-email">{sellerData.seller.email}</p>
                                                        {sellerData.seller.storeName && (
                                                            <p className="seller-store">{sellerData.seller.storeName}</p>
                                                        )}
                                                        <p className="google-ads-info">
                                                            Google Ads: {sellerData.googleAds.customerName} ({sellerData.googleAds.customerId})
                                                        </p>
                                                        {sellerData.merchant && (
                                                            <p className="merchant-info">
                                                                Merchant: {sellerData.merchant.name} ({sellerData.merchant.id})
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="seller-summary">
                                                    <div className="summary-item">
                                                        <span className="label">Spesa Totale:</span>
                                                        <span className="value">{formatCurrency(sellerData.totalSpending)}</span>
                                                    </div>
                                                    <div className="summary-item">
                                                        <span className="label">Campagne:</span>
                                                        <span className="value">{sellerData.campaignCount}</span>
                                                    </div>
                                                    <div className="expand-icon">
                                                        {expandedSeller === sellerData.seller.id ? '▼' : '▶'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Campagne espandibili */}
                                            {expandedSeller === sellerData.seller.id && (
                                                <div className="campaigns-section">
                                                                                        {sellerData.error ? (
                                        <div className={`error-message ${sellerData.errorType === 'developer_token_not_approved' ? 'developer-token-error' : ''} ${sellerData.errorType === 'oauth_expired' || sellerData.errorType === 'oauth_invalid' ? 'oauth-error' : ''}`}>
                                            <p>
                                                {sellerData.errorType === 'developer_token_not_approved' ? (
                                                    <>
                                                        <strong>⚠️ Developer Token non approvato</strong><br />
                                                        Il developer token è configurato solo per account di test. 
                                                        Per accedere ai dati di produzione, richiedi l'upgrade a Basic o Standard access su 
                                                        <a href="https://developers.google.com/google-ads/api/docs/first-call/dev-token" target="_blank" rel="noopener noreferrer">
                                                            Google Ads API Center
                                                        </a>.
                                                    </>
                                                ) : sellerData.errorType === 'oauth_expired' || sellerData.errorType === 'oauth_invalid' ? (
                                                    <>
                                                        <strong>🔐 Problema di Autenticazione OAuth</strong><br />
                                                        Il token di accesso è scaduto o non valido. 
                                                        Il seller deve riconnettere il proprio account Google Ads dalla pagina del profilo.
                                                    </>
                                                ) : (
                                                    `Errore nel recupero delle campagne: ${sellerData.error}`
                                                )}
                                            </p>
                                        </div>
                                    ) : sellerData.campaigns.length === 0 ? (
                                                        <div className="no-campaigns">
                                                            <p>Nessuna campagna trovata per questo intervallo di date.</p>
                                                        </div>
                                                    ) : (
                                                        <div className="campaigns-table">
                                                            <table>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Campagna</th>
                                                                        <th>Tipo</th>
                                                                        <th>Spesa</th>
                                                                        <th>Impressioni</th>
                                                                        <th>Clicks</th>
                                                                        <th>Conversioni</th>
                                                                        <th>CTR</th>
                                                                        <th>CPC</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {sellerData.campaigns.map((campaign) => (
                                                                        <tr key={campaign.id}>
                                                                            <td>
                                                                                <div className="campaign-name">
                                                                                    <strong>{campaign.name}</strong>
                                                                                    <span className="campaign-id">ID: {campaign.id}</span>
                                                                                    <button 
                                                                                        className="btn btn-sm btn-outline-primary campaign-detail-btn"
                                                                                        onClick={() => openCampaignDetail(campaign, sellerData)}
                                                                                        title="Visualizza dettaglio campagna"
                                                                                    >
                                                                                        📊 Dettaglio
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div className="campaign-type-info">
                                                                                    <span className={`channel-badge ${getChannelTypeColor(campaign.channelType)}`}>
                                                                                        {getChannelTypeLabel(campaign.channelType, campaign.channelSubType)}
                                                                                    </span>
                                                                                    {campaign.channelType === 'SHOPPING' && sellerData.merchant && (
                                                                                        <div className="merchant-info-small">
                                                                                            <small>Merchant: {sellerData.merchant.name}</small>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                            <td className="spending-cell">
                                                                                <strong>{formatCurrency(campaign.spending)}</strong>
                                                                            </td>
                                                                            <td>{formatNumber(campaign.impressions)}</td>
                                                                            <td>{formatNumber(campaign.clicks)}</td>
                                                                            <td>{campaign.conversions.toFixed(2)}</td>
                                                                            <td>
                                                                                {campaign.impressions > 0 
                                                                                    ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) + '%'
                                                                                    : '0%'
                                                                                }
                                                                            </td>
                                                                            <td>
                                                                                {campaign.clicks > 0 
                                                                                    ? formatCurrency(campaign.spending / campaign.clicks)
                                                                                    : '€0.00'
                                                                                }
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Modal per il dettaglio della campagna */}
                {selectedCampaign && (
                    <CampaignDetailChart
                        campaign={selectedCampaign.campaign}
                        customerId={selectedCampaign.customerId}
                        startDate={selectedCampaign.startDate}
                        endDate={selectedCampaign.endDate}
                        onClose={closeCampaignDetail}
                    />
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminGoogleAdsSpendingPage; 